import { RefObject, useLayoutEffect } from "react";

/** Share available space, never cap slides. Intrinsic/accessible content may grow.
    Runs once per load — slide changes no longer re-measure or re-observe. */
export function useBannerStage(root: RefObject<HTMLElement>, loading: boolean) {
  useLayoutEffect(() => {
    const section = root.current;
    const header = document.querySelector("header");
    const navigation = section?.querySelector(".banner-navigation");
    if (!section || !header || !navigation || loading) return;
    let frame = 0;
    const update = () => {
      const viewport = window.visualViewport?.scale === 1 ? Math.min(window.visualViewport.height, window.innerHeight) : document.documentElement.clientHeight;
      const wide = window.matchMedia("(min-width: 1024px)").matches;
      const navStyle = getComputedStyle(navigation);
      const navSpace = navigation.getBoundingClientRect().height + parseFloat(navStyle.marginTop);
      const bottom = parseFloat(getComputedStyle(section).paddingBottom) || 0;
      const available = Math.max(0, viewport - header.getBoundingClientRect().height - navSpace - bottom);
      section.style.setProperty("--banner-stage-space", `${wide ? Math.min(available, 768) : available}px`);
      section.style.setProperty("--journey-available", `${Math.max(0, viewport - header.getBoundingClientRect().height - bottom)}px`);
      // Reserve the longest ordinary copy across mobile siblings, not a spacer
      // above navigation. Copy remains intrinsically sized when fonts enlarge.
      if (!wide) {
        const copies = Array.from(section.querySelectorAll<HTMLElement>(".banner-photo-layout .banner-copy"));
        for (const copy of copies) copy.style.minHeight = "0px";
        const copyHeight = Math.max(0, ...copies.map(copy => copy.getBoundingClientRect().height));
        for (const copy of copies) copy.style.removeProperty("min-height");
        section.style.setProperty("--banner-copy-space", `${copyHeight}px`);
      }
    };
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(header); observer.observe(navigation);
    section.querySelectorAll(".banner-copy").forEach(copy => observer.observe(copy));
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    update();
    return () => { cancelAnimationFrame(frame); observer.disconnect(); window.removeEventListener("resize", measure); window.visualViewport?.removeEventListener("resize", measure); };
  }, [root, loading]);
}